// Yahoo names a subfolder with its parent's path: "Y2000-2009/Y2003" is Y2003 inside Y2000-2009. The folder list
// shows them as a tree whose parents open and close.

import type { Folder } from "./api";

/** Yahoo's IMAP hierarchy delimiter. */
const SEPARATOR = "/";

export interface FolderNode {
  /** The full name, "Y2000-2009/Y2003". */
  path: string;
  /** The last part, shown in the list: "Y2003". */
  label: string;
  depth: number;
  /** Left out for a parent that only the names of its subfolders imply. */
  folder?: Folder;
  children: FolderNode[];
}

/** The folders as a tree, siblings in the order the server lists them (Inbox first, then by name). */
export function folderTree(folders: Folder[]): FolderNode[] {
  const roots: FolderNode[] = [];
  const nodes = new Map<string, FolderNode>();
  const node = (path: string): FolderNode => {
    const known = nodes.get(path);
    if (known) return known;
    const cut = path.lastIndexOf(SEPARATOR);
    const parent = cut > 0 && cut < path.length - 1 ? node(path.slice(0, cut)) : null;
    const created: FolderNode = { path, label: parent ? path.slice(cut + 1) : path, depth: parent ? parent.depth + 1 : 0, children: [] };
    nodes.set(path, created);
    (parent ? parent.children : roots).push(created);
    return created;
  };
  for (const f of folders) node(f.name).folder = f;
  return roots;
}

/** The parents of a folder, outermost first: "a/b/c" gives ["a", "a/b"]. */
export function ancestors(path: string): string[] {
  const out: string[] = [];
  for (let cut = path.indexOf(SEPARATOR); cut > 0 && cut < path.length - 1; cut = path.indexOf(SEPARATOR, cut + 1)) {
    out.push(path.slice(0, cut));
  }
  return out;
}

/** Unread mail in a folder and all its subfolders: what a closed parent shows. */
export function unreadBelow(node: FolderNode): number {
  return node.children.reduce((sum, child) => sum + unreadBelow(child), node.folder?.unread ?? 0);
}

/** The sync error of a folder or else of the first of its subfolders that has one. */
export function errorBelow(node: FolderNode): string | null {
  if (node.folder?.last_error) return node.folder.last_error;
  for (const child of node.children) {
    const error = errorBelow(child);
    if (error) return error;
  }
  return null;
}
